import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { 
  Search, User, Phone, Mail, GripVertical, Calendar, Loader2,
  UserPlus, Home, FileText, CreditCard, Factory, Truck, Wrench, Star, CheckCircle, X, ChevronDown, ChevronUp, PoundSterling
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: 'new_lead', label: 'New Lead', icon: UserPlus, color: '#6366f1', bg: '#eef2ff' },
  { id: 'home_visit_booked', label: 'Home Visit Booked', icon: Home, color: '#8b5cf6', bg: '#f5f3ff' },
  { id: 'quoted', label: 'Quoted', icon: FileText, color: '#3b82f6', bg: '#eff6ff' },
  { id: 'paid_deposit', label: 'Paid Deposit', icon: CreditCard, color: '#f59e0b', bg: '#fffbeb' },
  { id: 'paid_in_full', label: 'Paid in Full', icon: PoundSterling, color: '#10b981', bg: '#ecfdf5' },
  { id: 'in_production', label: 'In Production', icon: Factory, color: '#f97316', bg: '#fff7ed' },
  { id: 'shipping', label: 'Shipping', icon: Truck, color: '#06b6d4', bg: '#ecfeff' },
  { id: 'installation_booked', label: 'Installation Booked', icon: Wrench, color: '#8CB89F', bg: '#f0fdf4' },
  { id: 'completed_review_request', label: 'Completed (Review)', icon: Star, color: '#eab308', bg: '#fefce8' },
  { id: 'completed_no_review', label: 'Completed', icon: CheckCircle, color: '#22c55e', bg: '#f0fdf4' },
];

// Job Card Component
const JobCard = ({ job, onDragStart, onEdit, onUpdateETA }) => {
  const [expanded, setExpanded] = useState(false);
  const f = job.financials || {};
  const hasPaid = (f.total_paid || 0) > 0;
  const balanceDue = f.balance_due || 0;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', job.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(job.id);
      }}
      className="bg-white rounded-lg border border-stone-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
      data-testid={`job-card-${job.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="w-3.5 h-3.5 text-stone-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="min-w-0">
            <p className="font-semibold text-stone-800 text-sm truncate">{job.customer_name}</p>
            <p className="text-[10px] text-stone-400">{job.job_number}</p>
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="text-stone-400 hover:text-stone-600">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Quick Info */}
      <div className="space-y-1 text-xs">
        {job.customer_phone && (
          <div className="flex items-center gap-1.5 text-stone-500">
            <Phone className="w-3 h-3" /> <span className="truncate">{job.customer_phone}</span>
          </div>
        )}
        {job.customer_email && (
          <div className="flex items-center gap-1.5 text-stone-500">
            <Mail className="w-3 h-3" /> <span className="truncate">{job.customer_email}</span>
          </div>
        )}
      </div>

      {/* Financial Summary */}
      {f.quote_total > 0 && (
        <div className="mt-2 pt-2 border-t border-stone-100">
          <div className="flex justify-between text-xs">
            <span className="text-stone-500">Quote</span>
            <span className="font-semibold text-stone-700">£{f.quote_total?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
          </div>
          {hasPaid && (
            <div className="flex justify-between text-xs mt-0.5">
              <span className="text-green-600">Paid</span>
              <span className="font-semibold text-green-600">£{f.total_paid?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          {balanceDue > 0 && (
            <div className="flex justify-between text-xs mt-0.5">
              <span className="text-amber-600">Outstanding</span>
              <span className="font-semibold text-amber-600">£{balanceDue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>
      )}

      {/* Windows count */}
      {job.windows_count > 0 && (
        <div className="mt-1.5">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{job.windows_count} window{job.windows_count !== 1 ? 's' : ''}</Badge>
        </div>
      )}

      {/* ETA badge */}
      {job.estimated_arrival_date && (
        <div className="mt-1.5">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-cyan-300 text-cyan-700">
            <Calendar className="w-2.5 h-2.5 mr-1" />ETA: {job.estimated_arrival_date}
          </Badge>
        </div>
      )}

      {/* Expanded Section */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
          {job.customer_address && (
            <p className="text-xs text-stone-500">{job.customer_address}</p>
          )}
          {job.notes && (
            <p className="text-xs text-stone-400 italic">{job.notes}</p>
          )}
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={(e) => { e.stopPropagation(); onEdit(job); }}>
              Edit
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={(e) => { e.stopPropagation(); onUpdateETA(job); }}>
              <Calendar className="w-3 h-3 mr-1" />ETA
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Pipeline Column
const PipelineColumn = ({ stage, jobs, onDrop, onDragStart, onEdit, onUpdateETA, stageTotals }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const Icon = stage.icon;
  const totals = stageTotals || {};
  const stageJobs = jobs.filter(j => j.stage === stage.id);

  return (
    <div
      className={`flex-shrink-0 w-[260px] rounded-xl border transition-all ${
        isDragOver ? 'border-dashed border-2 border-blue-400 bg-blue-50/50' : 'border-stone-200 bg-stone-50/50'
      }`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const jobId = e.dataTransfer.getData('text/plain'); onDrop(jobId, stage.id); }}
      data-testid={`pipeline-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-stone-200">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: stage.bg }}>
            <Icon className="w-3.5 h-3.5" style={{ color: stage.color }} />
          </div>
          <span className="font-semibold text-xs text-stone-700 truncate">{stage.label}</span>
          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 bg-stone-200">{stageJobs.length}</Badge>
        </div>
        {totals.total_value > 0 && (
          <div className="text-[10px] text-stone-400 pl-8">
            £{totals.total_value?.toLocaleString('en-GB', { minimumFractionDigits: 0 })} total
            {totals.total_outstanding > 0 && (
              <span className="text-amber-500 ml-1">| £{totals.total_outstanding?.toLocaleString('en-GB', { minimumFractionDigits: 0 })} due</span>
            )}
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto min-h-[80px]">
        {stageJobs.map(job => (
          <JobCard key={job.id} job={job} onDragStart={onDragStart} onEdit={onEdit} onUpdateETA={onUpdateETA} />
        ))}
        {stageJobs.length === 0 && (
          <div className="text-center py-6 text-stone-300 text-xs">
            Drop jobs here
          </div>
        )}
      </div>
    </div>
  );
};


const OrderPipeline = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dragJobId, setDragJobId] = useState(null);
  const [stageTotals, setStageTotals] = useState({});
  
  // Edit dialog
  const [editJob, setEditJob] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // ETA dialog
  const [etaJob, setEtaJob] = useState(null);
  const [etaDate, setEtaDate] = useState('');
  const [etaSaving, setEtaSaving] = useState(false);

  // Add job dialog
  const [showAddJob, setShowAddJob] = useState(false);
  const [addForm, setAddForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', customer_postcode: '', windows_count: '', notes: '' });

  const fetchJobs = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/api/jobs/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: search ? { search } : {}
      });
      if (res.data.jobs) {
        setJobs(res.data.jobs);
        setStageTotals(res.data.stage_totals || {});
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Drag and drop stage change
  const handleDrop = async (jobId, newStage) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job || job.stage === newStage) return;

    // Optimistic update
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, stage: newStage } : j));

    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/api/jobs/${jobId}/stage?stage=${newStage}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Job moved', description: `Moved to ${STAGES.find(s => s.id === newStage)?.label}` });
      fetchJobs();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to move job', variant: 'destructive' });
      fetchJobs();
    }
  };

  // Edit job
  const openEdit = (job) => {
    setEditJob(job);
    setEditForm({
      customer_name: job.customer_name || '',
      customer_email: job.customer_email || '',
      customer_phone: job.customer_phone || '',
      customer_address: job.customer_address || '',
      customer_postcode: job.customer_postcode || '',
      windows_count: job.windows_count || '',
      notes: job.notes || '',
      stage: job.stage,
    });
  };

  const saveEdit = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/api/jobs/${editJob.id}`, {
        ...editForm,
        windows_count: editForm.windows_count ? parseInt(editForm.windows_count) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Job updated' });
      setEditJob(null);
      fetchJobs();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update job', variant: 'destructive' });
    }
  };

  // ETA update
  const openETA = (job) => {
    setEtaJob(job);
    setEtaDate(job.estimated_arrival_date || '');
  };

  const saveETA = async () => {
    if (!etaDate) return;
    setEtaSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.patch(`${API}/api/jobs/${etaJob.id}/estimated-arrival`, 
        { estimated_arrival_date: etaDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const emailType = res.data.email_sent;
      if (emailType === 'delay_apology') {
        toast({ title: 'ETA updated', description: 'Delay apology email sent to customer' });
      } else if (emailType === 'eta_update') {
        toast({ title: 'ETA updated', description: 'Delivery date email sent to customer' });
      } else {
        toast({ title: 'ETA updated' });
      }
      setEtaJob(null);
      fetchJobs();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update ETA', variant: 'destructive' });
    } finally {
      setEtaSaving(false);
    }
  };

  // Add job
  const addJob = async () => {
    if (!addForm.customer_name) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/api/jobs/`, {
        ...addForm,
        windows_count: addForm.windows_count ? parseInt(addForm.windows_count) : null,
        stage: 'new_lead',
        is_new_customer: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Job created' });
      setShowAddJob(false);
      setAddForm({ customer_name: '', customer_email: '', customer_phone: '', customer_address: '', customer_postcode: '', windows_count: '', notes: '' });
      fetchJobs();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to create job', variant: 'destructive' });
    }
  };

  // Delete job
  const deleteJob = async () => {
    if (!editJob) return;
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api/jobs/${editJob.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Job deleted' });
      setEditJob(null);
      fetchJobs();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#8CB89F]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="order-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-stone-800">Job Pipeline</h2>
          <p className="text-sm text-stone-500">{jobs.length} total jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64 h-9"
              data-testid="pipeline-search"
            />
          </div>
          <Button onClick={() => setShowAddJob(true)} className="bg-[#2F4F4F] hover:bg-[#3d6363] h-9" data-testid="add-job-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /> New Job
          </Button>
        </div>
      </div>

      {/* Pipeline Columns */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 260px)' }}>
        {STAGES.map(stage => (
          <PipelineColumn
            key={stage.id}
            stage={stage}
            jobs={jobs}
            stageTotals={stageTotals[stage.id]}
            onDrop={handleDrop}
            onDragStart={setDragJobId}
            onEdit={openEdit}
            onUpdateETA={openETA}
          />
        ))}
      </div>

      {/* Edit Job Dialog */}
      <Dialog open={!!editJob} onOpenChange={(open) => !open && setEditJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Job — {editJob?.job_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Customer Name</label>
              <Input value={editForm.customer_name} onChange={(e) => setEditForm(f => ({ ...f, customer_name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Email</label>
                <Input value={editForm.customer_email} onChange={(e) => setEditForm(f => ({ ...f, customer_email: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Phone</label>
                <Input value={editForm.customer_phone} onChange={(e) => setEditForm(f => ({ ...f, customer_phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Address</label>
              <Input value={editForm.customer_address} onChange={(e) => setEditForm(f => ({ ...f, customer_address: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Postcode</label>
                <Input value={editForm.customer_postcode} onChange={(e) => setEditForm(f => ({ ...f, customer_postcode: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Windows</label>
                <Input type="number" value={editForm.windows_count} onChange={(e) => setEditForm(f => ({ ...f, windows_count: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Stage</label>
              <select
                value={editForm.stage}
                onChange={(e) => setEditForm(f => ({ ...f, stage: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Notes</label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm h-20 resize-none"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="destructive" size="sm" onClick={deleteJob}>Delete</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditJob(null)}>Cancel</Button>
              <Button onClick={saveEdit} className="bg-[#2F4F4F]">Save</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ETA Dialog */}
      <Dialog open={!!etaJob} onOpenChange={(open) => !open && setEtaJob(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Estimated Arrival Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">{etaJob?.customer_name}</p>
            {etaJob?.estimated_arrival_date && (
              <p className="text-xs text-stone-400">Current: {etaJob.estimated_arrival_date}</p>
            )}
            <Input
              type="date"
              value={etaDate}
              onChange={(e) => setEtaDate(e.target.value)}
            />
            <p className="text-xs text-stone-400">
              {etaJob?.estimated_arrival_date 
                ? etaDate > etaJob.estimated_arrival_date 
                  ? '⚠️ Date moved later — delay apology email will be sent'
                  : '✉️ Updated ETA email will be sent to customer'
                : '✉️ ETA notification email will be sent to customer'
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEtaJob(null)}>Cancel</Button>
            <Button onClick={saveETA} disabled={!etaDate || etaSaving} className="bg-[#2F4F4F]">
              {etaSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Update ETA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Job Dialog */}
      <Dialog open={showAddJob} onOpenChange={setShowAddJob}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Customer Name *</label>
              <Input value={addForm.customer_name} onChange={(e) => setAddForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="John Smith" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Email</label>
                <Input value={addForm.customer_email} onChange={(e) => setAddForm(f => ({ ...f, customer_email: e.target.value }))} placeholder="john@example.com" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Phone</label>
                <Input value={addForm.customer_phone} onChange={(e) => setAddForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="07123 456789" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Address</label>
              <Input value={addForm.customer_address} onChange={(e) => setAddForm(f => ({ ...f, customer_address: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600">Postcode</label>
                <Input value={addForm.customer_postcode} onChange={(e) => setAddForm(f => ({ ...f, customer_postcode: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Windows</label>
                <Input type="number" value={addForm.windows_count} onChange={(e) => setAddForm(f => ({ ...f, windows_count: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Notes</label>
              <textarea value={addForm.notes} onChange={(e) => setAddForm(f => ({ ...f, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm h-20 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddJob(false)}>Cancel</Button>
            <Button onClick={addJob} disabled={!addForm.customer_name} className="bg-[#2F4F4F]">Create Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderPipeline;
